import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type { RootState } from "@/store/types";
import {
  setAccessToken,
  setRefreshToken,
  setProjectAccessToken,
  setProjectRefreshToken,
  forceLogout,
  updateUserDetailsFromToken,
} from "@/store/slices/auth";
import {
  API_URLS,
  API_SETTINGS,
  API_ERRORS,
  AUTH_RETRY_CONFIG,
  type ApiUrlKey,
} from "./config";
import { extractUserDataFromToken } from "./utils";
import { LogoutService } from "./services";

export interface ExtraOptions {
  skipAuthRetry?: boolean;
}
type ApiErrorStatus =
  | number
  | "FETCH_ERROR"
  | "TIMEOUT_ERROR"
  | "PARSING_ERROR"
  | "CUSTOM_ERROR";
interface ApiInterface {
  getState: () => unknown;
  dispatch: (action: unknown) => void;
}

// Error message mapping - defined once outside function
const ERROR_MAP: Readonly<Record<ApiErrorStatus, string>> = {
  400: "Bad request. Please check your input.",
  401: API_ERRORS.UNAUTHORIZED,
  403: API_ERRORS.FORBIDDEN,
  404: API_ERRORS.NOT_FOUND,
  429: API_ERRORS.RATE_LIMIT,
  500: API_ERRORS.SERVER,
  502: API_ERRORS.SERVER,
  503: API_ERRORS.SERVER,
  FETCH_ERROR: API_ERRORS.NETWORK,
  TIMEOUT_ERROR: API_ERRORS.TIMEOUT,
  PARSING_ERROR: "Failed to parse response.",
  CUSTOM_ERROR: API_ERRORS.UNKNOWN,
};

let refreshPromise: Promise<boolean> | null = null;
interface RefreshSessionState {
  consecutiveFailures: number;
  lastSuccessTimestamp: number;
  isRefreshing: boolean;
}

const refreshState: RefreshSessionState = {
  consecutiveFailures: 0,
  lastSuccessTimestamp: Date.now(),
  isRefreshing: false,
};

/**
 * Reset auth failure counter - call this on successful login or successful refresh
 */
export const resetAuthFailureCounter = (): void => {
  refreshState.consecutiveFailures = 0;
  refreshState.lastSuccessTimestamp = Date.now();
  if (API_SETTINGS.debugMode) {
    console.log("[Auth] Failure counter reset");
  }
};

/**
 * Calculate delay with exponential backoff
 */
const calculateBackoffDelay = (attempt: number): number => {
  const delay =
    AUTH_RETRY_CONFIG.INITIAL_DELAY_MS *
    Math.pow(AUTH_RETRY_CONFIG.BACKOFF_MULTIPLIER, attempt);
  return Math.min(delay, AUTH_RETRY_CONFIG.MAX_DELAY_MS);
};

/**
 * Sleep for specified milliseconds
 */
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Refresh failure reasons - determines if we should retry or logout
 */
type RefreshFailureReason =
  | "TOKEN_INVALID" // 401 - Token is invalid/expired, logout immediately
  | "TOKEN_FORBIDDEN" // 403 - Token is forbidden, logout immediately
  | "NO_CREDENTIALS" // Missing refresh token or CIN, logout immediately
  | "SERVER_ERROR" // 500+ - Server error, can retry
  | "NETWORK_ERROR" // Network/timeout error, can retry
  | "PARSE_ERROR" // Response parse error, can retry
  | "UNKNOWN"; // Unknown error, can retry

interface RefreshAttemptResult {
  success: boolean;
  shouldRetry: boolean;
  reason?: RefreshFailureReason;
}

/**
 * Attempt to refresh the access token
 * Returns: { success: boolean, shouldRetry: boolean, reason?: string }
 */
const attemptTokenRefresh = async (
  api: ApiInterface,
): Promise<RefreshAttemptResult> => {
  try {
    const state = api.getState() as RootState;
    const projectRefreshToken = state.auth?.projectRefreshToken;
    const loginRefreshToken = state.auth?.loginResponse?.refreshToken;
    const isUsingProjectToken = !!projectRefreshToken;
    const refreshTokenToUse = projectRefreshToken || loginRefreshToken;

    if (!refreshTokenToUse) {
      console.warn("[Auth] No refresh token available - forcing logout");
      return { success: false, shouldRetry: false, reason: "NO_CREDENTIALS" };
    }
    if (API_SETTINGS.debugMode) {
      console.log(`[Auth] Attempting token refresh (${isUsingProjectToken ? "project" : "login"} token)...`);
    }
    const response = await fetch(`${API_URLS.main}auth/refreshToken`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refreshToken: refreshTokenToUse,
      }),
    });

    // Success case
    if (response.ok) {
      try {
        const data = await response.json();
        if (data?.token) {
          if (isUsingProjectToken) {
            api.dispatch(setProjectAccessToken(data.token));
            if (data?.refreshToken) {
              api.dispatch(setProjectRefreshToken(data.refreshToken));
            }
          } else {
            api.dispatch(setAccessToken(data.token));
            if (data?.refreshToken) {
              api.dispatch(setRefreshToken(data.refreshToken));
            }
          }

          // Extract user data from the new access token and update Redux state
          const userData = extractUserDataFromToken(data.token);
          if (userData) {
            api.dispatch(updateUserDetailsFromToken({
              firstName: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
              role: userData.role,
              isAgent: userData.isAgent,
              status: userData.status,
              ciNo: userData.ciNo,
              profilePicUrl: userData.profilePicUrl,
            }));
            if (API_SETTINGS.debugMode) {
              console.log("[Auth] Updated user details from token:", {
                firstName: userData.firstName,
                lastName: userData.lastName,
              });
            }
          }

          // Reset failure counter on success
          resetAuthFailureCounter();
          console.log(`[Auth] Token refresh successful (${isUsingProjectToken ? "project" : "login"} token)`);
          return { success: true, shouldRetry: false };
        }
        // Response OK but no token - treat as parse error, can retry
        console.warn("[Auth] Refresh response OK but no token in response");
        return { success: false, shouldRetry: true, reason: "PARSE_ERROR" };
      } catch (parseError) {
        console.warn("[Auth] Failed to parse refresh response:", parseError);
        return { success: false, shouldRetry: true, reason: "PARSE_ERROR" };
      }
    }

    // 401 means refresh token is expired/invalid - DO NOT retry
    // The token is permanently invalid, retrying won't help
    if (response.status === 401) {
      console.error(
        "[Auth] Refresh token expired/invalid (401) - FORCING IMMEDIATE LOGOUT",
      );
      return { success: false, shouldRetry: false, reason: "TOKEN_INVALID" };
    }

    // 403 means forbidden - DO NOT retry
    if (response.status === 403) {
      console.error(
        "[Auth] Refresh token forbidden (403) - FORCING IMMEDIATE LOGOUT",
      );
      return { success: false, shouldRetry: false, reason: "TOKEN_FORBIDDEN" };
    }

    // Server errors (5xx) - can retry
    if (response.status >= 500) {
      console.warn(
        `[Auth] Server error (${response.status}) during refresh - will retry`,
      );
      return { success: false, shouldRetry: true, reason: "SERVER_ERROR" };
    }

    // Other 4xx errors - generally should not retry
    if (response.status >= 400) {
      console.warn(
        `[Auth] Client error (${response.status}) during refresh - forcing logout`,
      );
      return { success: false, shouldRetry: false, reason: "UNKNOWN" };
    }

    // Unexpected status code - can retry
    console.warn(
      `[Auth] Unexpected status (${response.status}) during refresh - will retry`,
    );
    return { success: false, shouldRetry: true, reason: "UNKNOWN" };
  } catch (error) {
    // Network errors, timeouts, etc. - can retry
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.warn(
      `[Auth] Network/timeout error during refresh: ${errorMessage} - will retry`,
    );
    return { success: false, shouldRetry: true, reason: "NETWORK_ERROR" };
  }
};

/**
 * Perform token refresh with retry logic
 *
 * RETRY BEHAVIOR:
 * - 401/403 from refresh endpoint: Immediate logout, NO retries (token is permanently invalid)
 * - Missing credentials: Immediate logout, NO retries
 * - Server errors (5xx): Retry up to MAX_RETRIES times with exponential backoff
 * - Network errors: Retry up to MAX_RETRIES times with exponential backoff
 * - After MAX_RETRIES consecutive failures: Force logout and clear all storage
 *
 * "Consecutive" means failures within a single refresh session (single call to performTokenRefresh)
 */
const performTokenRefresh = async (api: ApiInterface): Promise<boolean> => {
  // If there's already a refresh in progress, wait for it
  if (refreshPromise) {
    if (API_SETTINGS.debugMode) {
      console.log("[Auth] Refresh already in progress, waiting...");
    }
    return refreshPromise;
  }

  // Mark that we're starting a refresh session
  refreshState.isRefreshing = true;
  refreshPromise = (async () => {
    const maxRetries = AUTH_RETRY_CONFIG.MAX_RETRIES;
    let consecutiveFailuresInSession = 0;
    console.log(
      `[Auth] Starting token refresh session (max ${maxRetries} retries)`,
    );
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      console.log(`[Auth] Token refresh attempt ${attempt + 1}/${maxRetries}`);
      const result = await attemptTokenRefresh(api);
      // SUCCESS - Reset counters and return
      if (result.success) {
        console.log("[Auth] Token refresh session completed successfully");
        refreshState.consecutiveFailures = 0;
        refreshState.isRefreshing = false;
        return true;
      }
      // Track this failure
      consecutiveFailuresInSession++;
      refreshState.consecutiveFailures++;
      // NON-RETRYABLE FAILURE - Logout immediately
      // This includes: 401, 403, missing credentials, other 4xx errors
      if (!result.shouldRetry) {
        console.error(
          `[Auth] Non-retryable failure (${result.reason}) - forcing logout immediately`,
        );
        refreshState.isRefreshing = false;
        return false;
      }
      // RETRYABLE FAILURE - Check if we should continue
      console.warn(
        `[Auth] Retryable failure (${result.reason}) - ${consecutiveFailuresInSession}/${maxRetries} consecutive failures`,
      );
      // If we haven't reached max retries, wait and try again
      if (attempt < maxRetries - 1) {
        const delay = calculateBackoffDelay(attempt);
        console.log(
          `[Auth] Waiting ${delay}ms before retry ${attempt + 2}/${maxRetries}...`,
        );
        await sleep(delay);
      }
    }
    // EXHAUSTED ALL RETRIES - This means we had MAX_RETRIES consecutive retryable failures
    console.error(
      `[Auth] Token refresh FAILED after ${maxRetries} consecutive retries - forcing logout`,
    );
    console.error(
      `[Auth] Total consecutive failures in session: ${consecutiveFailuresInSession}`,
    );
    console.error(
      `[Auth] Total consecutive failures overall: ${refreshState.consecutiveFailures}`,
    );
    refreshState.isRefreshing = false;
    return false;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

// Create base query with auth and error handling
export const createBaseQuery = (
  baseUrl: ApiUrlKey = "main",
): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError | { status: number | string; data: unknown },
  ExtraOptions
> => {
  const baseQuery = fetchBaseQuery({
    baseUrl: API_URLS[baseUrl],
    timeout: API_SETTINGS.timeout,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Accept", "application/json, text/plain, */*");
      const shouldSkipContentType =
        headers.has("Content-Type") || headers.has("X-Skip-Content-Type");
      if (!shouldSkipContentType) {
        headers.set("Content-Type", "application/json");
      }
      headers.delete("X-Skip-Content-Type");
      const authState = (getState() as RootState).auth;
      const token = authState?.projectAccessToken ?? authState?.accessToken;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
        //  headers.set('x-cin', );
        if (API_SETTINGS.debugMode) {
          console.log(
            "[API] Using token (first 20 chars):",
            token.substring(0, 20) + "...",
          );
        }
      }
      return headers;
    },
  });

  return async (args, api, extraOptions = {}) => {
    let result = await baseQuery(args, api, extraOptions);
    // Handle 401 Unauthorized - attempt token refresh
    if (result.error?.status === 401 && !extraOptions.skipAuthRetry) {
      const state = api.getState() as RootState;
      if (state.auth?.accessToken) {
        console.log("[API] Got 401, attempting token refresh...");
        const refreshed = await performTokenRefresh(api);
        if (refreshed) {
          // Retry the original request with new token
          console.log("[API] Token refreshed, retrying original request");
          result = await baseQuery(args, api, extraOptions);
        } else {
          // Token refresh failed - perform comprehensive cleanup and force logout
          console.error(
            "[API] Token refresh failed - performing comprehensive cleanup and forcing logout",
          );
          LogoutService.setInProgress(true);
          try {
            // Await cleanup so storage is cleared before we return
            await LogoutService.performCleanup();
          } catch (err) {
            console.error("[API] Cleanup error during force logout:", err);
          }
          // Dispatch force logout action to clear Redux state
          api.dispatch(forceLogout());
          // Purge persisted state so isAuthenticated:true doesn't survive
          // Dynamic import to avoid circular dependency (base-query → store → APIs → base-query)
          try { const { persistor } = await import("@/store"); await persistor.purge(); } catch { /* ignore */ }
          // Reset the refresh state
          refreshState.consecutiveFailures = 0;
          refreshState.isRefreshing = false;
          LogoutService.setInProgress(false);
          return {
            error: { status: 401, data: { message: API_ERRORS.UNAUTHORIZED } },
          };
        }
      }
    }

    // Handle other errors - map to user-friendly messages
    if (result.error) {
      const { status } = result.error;
      const errorData = result.error.data as
        | Record<string, unknown>
        | undefined;
      const apiMessage =
        typeof errorData?.message === "string" ? errorData.message : undefined;
      if (API_SETTINGS.debugMode) {
        console.error("[API Error]", {
          status,
          errorData,
          endpoint: typeof args === "string" ? args : args.url,
        });
      }
      return {
        error: {
          status,
          data: {
            ...errorData,
            message:
              apiMessage ||
              ERROR_MAP[status as ApiErrorStatus] ||
              API_ERRORS.UNKNOWN,
          },
        },
      };
    }
    return result;
  };
};

export default createBaseQuery;
